import { Fragment, type ComponentPropsWithoutRef } from "react";

import { type FullAirHandlerUnit } from "@eisox/api/core-api";
import { cn, Field, Heading, Select } from "@eisox/design-system";
import { TargetIcon } from "@eisox/icons";
import { Link, useNavigate } from "@tanstack/react-router";

import { m } from "~/paraglide/messages";

import {
  getAirHandlerUnitGroupName,
  getAreas,
  getParentAirHandlerUnits,
  getSortedFullAirHandlerUnitGroups,
} from "../helpers";
import { Indicators } from "./indicators";

const NAME = "RightColumn";

interface RightColumnProps extends ComponentPropsWithoutRef<"div"> {
  buildingId: string;
  name: string;
  airHandlerUnit: FullAirHandlerUnit[];
  airHandlerUnits: FullAirHandlerUnit[];
  onAirHandlerUnitFocus?: (airHandlerUnitId: string) => void;
}

export const RightColumn: React.FC<RightColumnProps> = ({
  buildingId,
  name,
  airHandlerUnit,
  airHandlerUnits,
  onAirHandlerUnitFocus,
  className,
  ...props
}) => {
  const navigate = useNavigate();

  const primaryAhu = airHandlerUnit[0];
  if (!primaryAhu) return null;

  const parentAirHandlerUnits = getParentAirHandlerUnits(airHandlerUnits);
  const areas = getAreas(airHandlerUnits);

  const currentParentAirHandlerUnit =
    airHandlerUnit.length === 1 && primaryAhu.airHandlerUnitParentId !== undefined
      ? airHandlerUnits.find(ahu => ahu.id === primaryAhu.airHandlerUnitParentId)
      : undefined;
  const currentParentAirHandlerUnitId = currentParentAirHandlerUnit?.id ?? primaryAhu.id;

  const area = currentParentAirHandlerUnitId === primaryAhu.id ? undefined : primaryAhu.id;

  const getAreaParentName = (airHandlerUnitParentId?: string) =>
    airHandlerUnits.find(ahu => ahu.id === airHandlerUnitParentId)?.name;

  const parentAirHandlerUnitOptions = getSortedFullAirHandlerUnitGroups(parentAirHandlerUnits);

  const handleSelectAirHandlerUnit = (airHandlerUnitId: string) => {
    void navigate({
      to: "/buildings/$buildingId/air-handler-units/$airHandlerUnitId",
      params: { buildingId, airHandlerUnitId },
    });
  };

  return (
    <div
      {...props}
      className={cn(
        `min-h-0 p-5 flex flex-col overflow-hidden rounded-tl-[10px] rounded-bl-[10px] bg-primary-white [grid-area:column]`,
        className,
      )}
    >
      {area && currentParentAirHandlerUnit !== undefined ? (
        <Heading asChild as="h3" size={2} className={"gap-1.25 flex items-center"}>
          <Link
            to={"/buildings/$buildingId/air-handler-units/$airHandlerUnitId"}
            params={{ buildingId, airHandlerUnitId: currentParentAirHandlerUnitId }}
          >
            {currentParentAirHandlerUnit.name} <TargetIcon />
          </Link>
        </Heading>
      ) : parentAirHandlerUnits.length > 1 ? (
        <Select.Root
          key={currentParentAirHandlerUnitId}
          multiple={false}
          value={currentParentAirHandlerUnitId}
          onValueChange={handleSelectAirHandlerUnit}
        >
          <Select.Trigger className="flex-[0_0_auto]">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            {parentAirHandlerUnitOptions.length > 10 && <Select.Input />}
            <Select.Viewport>
              {parentAirHandlerUnitOptions.map(ahuGroup => {
                const firstAhu = ahuGroup[0];
                if (!firstAhu) return null;
                return (
                  <Select.Item key={firstAhu.id} value={firstAhu.id}>
                    {getAirHandlerUnitGroupName(ahuGroup)}
                  </Select.Item>
                );
              })}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      ) : (
        <Heading as="h3" size={2}>
          {name}
        </Heading>
      )}
      <div className="gap-5 mt-5 flex flex-[1_1_auto] flex-col overflow-x-hidden overflow-y-auto">
        {parentAirHandlerUnits.includes(primaryAhu) &&
          airHandlerUnits.length > 0 &&
          airHandlerUnit.map(ahu => (
            <Fragment key={ahu.id}>
              {airHandlerUnit.length > 1 && (
                <Heading as="h3" size={2}>
                  <button
                    type="button"
                    className="gap-1.25 flex cursor-pointer items-center text-left"
                    onClick={() => onAirHandlerUnitFocus?.(ahu.id)}
                  >
                    {ahu.name} <TargetIcon />
                  </button>
                </Heading>
              )}
              <Indicators buildingId={buildingId} airHandlerUnit={ahu} airHandlerUnits={airHandlerUnits} />
            </Fragment>
          ))}
        {areas.length > 0 && (
          <Field.Root>
            <Field.Label>{m.areas()}</Field.Label>
            <Select.Root key={area} multiple={false} value={area} onValueChange={handleSelectAirHandlerUnit}>
              <Select.Trigger className="w-full">
                <Select.Value placeholder={m.areas()} />
              </Select.Trigger>
              <Select.Content>
                {areas.length > 10 && <Select.Input />}
                <Select.Viewport>
                  {areas.map(a => (
                    <Select.Item key={a.id} value={a.id} className="gap-1.25 flex flex-wrap items-baseline">
                      {a.name}{" "}
                      <span className="text-sm text-primary-dark-gray">
                        {getAreaParentName(a.airHandlerUnitParentId)}
                      </span>
                      <Select.ItemText>{a.name}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Root>
          </Field.Root>
        )}
      </div>
    </div>
  );
};

RightColumn.displayName = NAME;
